// src/views/SdkDevView.tsx
import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Row, Col, message, Spin } from 'antd';
import SdkLoginContent from '../components/sdk/SdkLoginContent';
import SdkRegisterContent from '../components/sdk/SdkRegisterContent';
import SdkMgmtContent from '../components/sdk/SdkMgmtContent';

// 定义页面状态
type SdkPage = 'management' | 'login' | 'register';

// 定义 Props 类型
interface SdkDevViewProps extends WithTranslation { }

// 定义 State 类型
interface SdkDevViewState {
    currentPage: SdkPage;
    loading: boolean;
    isAuthenticated: boolean;
}

class SdkDevView extends React.Component<SdkDevViewProps, SdkDevViewState> {
    state: SdkDevViewState = {
        currentPage: 'login',
        loading: true,
        isAuthenticated: false,
    };

    componentDidMount() {
        this.checkToken();
    }

    // 验证 token
    checkToken = async () => {
        const token = localStorage.getItem('sdk_token');

        if (!token) {
            this.setState({
                currentPage: 'login',
                loading: false,
                isAuthenticated: false
            });
            return;
        }

        try {
            // 模拟验证 token 的 API 调用
            // 实际项目中替换为真实的验证接口
            const isValid = await this.verifyToken(token);

            if (isValid) {
                this.setState({
                    currentPage: 'management',
                    loading: false,
                    isAuthenticated: true
                });
            } else {
                // token 无效，清除本地存储
                localStorage.removeItem('sdk_token');
                localStorage.removeItem('sdk_user');
                this.setState({
                    currentPage: 'login',
                    loading: false,
                    isAuthenticated: false
                });
            }
        } catch (error) {
            console.error('Token 验证失败:', error);
            localStorage.removeItem('sdk_token');
            localStorage.removeItem('sdk_user');
            this.setState({
                currentPage: 'login',
                loading: false,
                isAuthenticated: false
            });
        }
    };

    // 模拟 token 验证
    verifyToken = async (token: string): Promise<boolean> => {
        // 模拟 API 请求延迟
        await new Promise((resolve) => setTimeout(resolve, 800));

        // 修复：确保返回 boolean 类型
        return token !== null && token !== undefined && token.length > 10;
    };
    // 导航到登录页
    navigateToLogin = () => {
        this.setState({ currentPage: 'login' });
    };

    // 导航到注册页
    navigateToRegister = () => {
        this.setState({ currentPage: 'register' });
    };

    // 导航回管理页
    navigateToManagement = () => {
        this.setState({ currentPage: 'management' });
    };

    // 登录成功回调
    handleLoginSuccess = (data: any) => {
        console.log('登录成功:', data);
        // 保存 token 和用户信息
        if (data.token) {
            localStorage.setItem('sdk_token', data.token);
        }
        if (data.user) {
            localStorage.setItem('sdk_user', JSON.stringify(data.user));
        }
        if (data.apps) {
            localStorage.setItem('sdk_apps', JSON.stringify(data.apps));
        }
        this.setState({
            currentPage: 'management',
            isAuthenticated: true
        });
        message.success('登录成功');
    };

    // 登录失败回调
    handleLoginFail = (error: any) => {
        console.log('登录失败:', error);
        // 清除可能存在的无效 token
        localStorage.removeItem('sdk_token');
        localStorage.removeItem('sdk_user');
        this.setState({ isAuthenticated: false });
    };

    // 注册成功回调
    handleRegisterSuccess = (data: any) => {
        console.log('注册成功:', data);
        message.success('注册成功，请登录');
        // 注册成功后跳转到登录页
        this.navigateToLogin();
    };

    // 注册失败回调
    handleRegisterFail = (error: any) => {
        console.log('注册失败:', error);
    };

    // 退出登录
    handleLogout = () => {
        localStorage.removeItem('sdk_token');
        localStorage.removeItem('sdk_user');
        this.setState({
            currentPage: 'login',
            isAuthenticated: false
        });
        message.info('已退出登录');
    };

    render() {
        const { t } = this.props;
        const { currentPage, loading, isAuthenticated } = this.state;

        // 加载中状态
        if (loading) {
            return (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100vh',
                    width: '100%'
                }}>
                    <Spin size="large" tip="验证中..." />
                </div>
            );
        }

        // 渲染对应的页面内容
        const renderContent = () => {
            switch (currentPage) {
                case 'login':
                    return (
                        <SdkLoginContent
                            trans={t}
                            onNavigateToRegister={this.navigateToRegister}
                            onLoginSuccess={this.handleLoginSuccess}
                            onLoginFail={this.handleLoginFail}
                        />
                    );
                case 'register':
                    return (
                        <SdkRegisterContent
                            trans={t}
                            onBack={this.navigateToLogin}
                            onRegisterSuccess={this.handleRegisterSuccess}
                            onRegisterFail={this.handleRegisterFail}
                        />
                    );
                case 'management':
                default:
                    return (
                        <SdkMgmtContent
                            onNavigateToLogin={this.navigateToLogin}
                            onNavigateToRegister={this.navigateToRegister}
                            onLogout={this.handleLogout}
                            isAuthenticated={isAuthenticated}
                        />
                    );
            }
        };

        // 判断是否是认证页面（登录/注册）
        const isAuthPage = currentPage === 'login' || currentPage === 'register';

        // 认证页面的列配置
        const authColProps = { xs: 22, sm: 16, md: 12, lg: 8, xl: 6 };

        // 管理页面使用全屏布局
        if (!isAuthPage) {
            return (
                <div style={{
                    height: '100vh',
                    width: '100%',
                    overflow: 'hidden',
                }}>
                    {renderContent()}
                </div>
            );
        }

        // 登录/注册页面布局
        return (
            <div style={{
                height: '100vh',
                width: '100%',
                overflow: 'hidden',
                background: '#f5f7fa',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '40px 24px',
            }}>
                <Row justify="center" style={{ width: '100%' }}>
                    <Col {...authColProps}>
                        {renderContent()}
                    </Col>
                </Row>
            </div>
        );
    }
}

export const SdkDevPage = withTranslation()(SdkDevView);